import {
  APPEARANCE_ATTRIBUTE,
  NOTICE_ROOT_ATTRIBUTE,
  buildTtsRootAttributes,
  getAppearanceLayout,
  resolveNoticeLayout,
  type NoticeAppearance,
  type ResolvedNoticeAppearance,
  type ResolvedNoticeLayout,
} from "../shared/appearance";
import {
  CONTROL_ATTRIBUTE,
  DEFAULT_ACCENT_COLOR,
  DEFAULT_HEADING_COLOR,
  DEFAULT_TEXT_COLOR,
  DOCK_ATTRIBUTE,
  DOCKED,
  LOGO_WIDTH_RATIO,
  MOTION_ATTRIBUTE,
  MOTION_OFF,
  NOTICE_ID_ATTRIBUTE,
  STYLE_RADII,
  SWITCH_CONTROL,
} from "../appearance/constants";
import { buildGlassSkin } from "../appearance/glass-skin";
import { resolveNoticeLook } from "../appearance/host-settings";
import {
  buildLayoutParts,
  isDocked,
  modalPosition,
  solidSurface,
} from "../appearance/layout-styles";
import { scaleFontSizes, textScaleFactor } from "../appearance/text-scale";
import { buildThemeCssVariables } from "../appearance/theme-vars";
import { buildVariantStyles } from "../appearance/variant-styles";
import type {
  ConfirmDialogSkin,
  LayoutParts,
  GlassParts,
  NoticeSkin,
  SelectionControlSkin,
  Style,
  StyleRadii,
} from "../appearance/types";
import { DEFAULT_SELECTION_CONTROL } from "../constants";
import { styles as classicStyles, type NoticeStyles } from "../styles/modal";
import type { ActiveConfig, SelectionControlType, Settings } from "../api/types";

export type ModalLookInput = Readonly<{
  noticeId: string;
  /** The notice once it is read; null while it loads. */
  content: ActiveConfig | null;
  /** The last appearance served for this notice, drawn while it loads. */
  cachedAppearance?: NoticeAppearance;
  settings?: Partial<Settings>;
  prefersDark: boolean;
  isMobile: boolean;
}>;

/** Footer and sub-flow buttons, each a style patch over the base button. */
export type ModalButtonStyles = Readonly<{
  base: Style;
  acceptAll: Style;
  acceptSelected: Style;
  decline: Style;
}>;

export type ModalLanguageStyles = Readonly<{
  chip: Style;
  menu: Style;
  selected: Style;
}>;

/**
 * Everything the modal draws with, resolved once per render from the console
 * appearance under the host's `settings`.
 */
export type ModalLook = Readonly<{
  appearance: ResolvedNoticeAppearance;
  layout: ResolvedNoticeLayout;
  skin: NoticeSkin<NoticeStyles> | null;
  /** Classic, the admin's palette, the style's shape and the text scale. */
  styles: NoticeStyles;
  /** Breakpoint sizes, spread after `styles`. */
  responsive: Record<string, Style>;
  /** The parts a layout choice draws with: glass's own, else the style's. */
  parts: LayoutParts;
  /** Glass lays the notice out as panels on a sheet; null otherwise. */
  sheetParts: GlassParts | null;
  docked: boolean;
  showsLoaderPill: boolean;
  textScale: number;
  headingColor: string;
  textColor: string;
  accentColor: string;
  linkColor: string;
  radii: StyleRadii;
  /** On the dialog, its overlay and its confirm step. */
  rootAttributes: Record<string, string>;
  /** Custom properties for the dialog root; set with `setProperty`. */
  cssVariables: Record<string, string>;
  overlayStyle: Style;
  modalStyle: Style;
  buttons: ModalButtonStyles;
  language: ModalLanguageStyles;
  controlSkin?: SelectionControlSkin;
  confirmSkin?: ConfirmDialogSkin;
  selectionControl: SelectionControlType;
  drawsSwitches: boolean;
  /** A yes/no pair, a dropdown and a switch are all taller than a checkbox. */
  roomyControls: boolean;
}>;

const baseResponsive = (isMobile: boolean): Record<string, Style> => ({
  modal: {
    width: isMobile ? "90%" : "700px",
    maxWidth: isMobile ? "90%" : "calc(100% - 32px)",
  },
  content: { margin: isMobile ? "20px" : "22px" },
  title: { fontSize: isMobile ? "16px" : "18px" },
  subTitle: { fontSize: isMobile ? "14px" : "16px" },
  optionTitle: { fontSize: isMobile ? "14px" : "16px" },
  optionDescription: { fontSize: isMobile ? "12px" : "14px" },
  dataElementText: { fontSize: isMobile ? "12px" : "14px" },
  privacyText: { fontSize: isMobile ? "14px" : "16px" },
  topRight: {
    fontSize: isMobile ? "11px" : "12px",
    padding: isMobile ? "3px 6px" : "3px 9px",
  },
  headerStack: { gap: isMobile ? "12px" : "10px" },
  logo: {
    maxWidth: isMobile ? "96px" : "140px",
    maxHeight: isMobile ? "28px" : "32px",
  },
  languageItem: {
    fontSize: isMobile ? "11px" : "12px",
    padding: isMobile ? "6px 10px" : "8px 12px",
  },
  middleSection: { paddingRight: isMobile ? "10px" : "15px" },
  button: {
    fontSize: isMobile ? "14px" : "16px",
    padding: isMobile ? "10px 20px" : "9px 45px",
  },
  dpoText: { fontSize: isMobile ? "13px" : "14px" },
});

export const buildModalLook = ({
  noticeId,
  content,
  cachedAppearance,
  settings,
  prefersDark,
  isMobile,
}: ModalLookInput): ModalLook => {
  const appearance = resolveNoticeLook(
    content ? content.appearance : cachedAppearance,
    settings,
    prefersDark,
  );
  const style = appearance.style;
  const colors = appearance.colors;
  const layout = resolveNoticeLayout(appearance);
  const docked = isDocked(layout, isMobile);
  const headingColor = colors.heading || DEFAULT_HEADING_COLOR;
  const textColor = colors.text || DEFAULT_TEXT_COLOR;
  const brandAccent = colors.toggle_on ?? colors.accept_all_bg;
  const isGlass = style === "glass";
  // Accept Selected's chain: the accent the brand paints its accents with.
  // Before the notice arrives its brand colour is unknown, so the loader
  // takes the neutral heading colour rather than flashing the SDK's default.
  const classicAccent =
    brandAccent || content?.primary_color || (content ? DEFAULT_ACCENT_COLOR : headingColor);
  // A skin paints switches, washes and rings in the accent, so it follows
  // Accept All's own chain: a host that themes only `acceptAll` sees one
  // brand colour across the notice, not two.
  const accentColor =
    colors.toggle_on ?? (isGlass ? colors.accept_all_bg || classicAccent : classicAccent);

  const variant = buildVariantStyles(classicStyles, style, colors, accentColor);
  const skin = isGlass
    ? buildGlassSkin(variant, {
        accent: accentColor,
        tint: colors.surface ?? colors.background,
        headingColor,
        textColor,
        isMobile,
        docked,
      })
    : null;
  const textScale = textScaleFactor(appearance);
  const styles = scaleFontSizes(skin?.styles ?? variant, textScale);

  const responsive: Record<string, Style> = baseResponsive(isMobile);
  for (const [key, patch] of Object.entries(skin?.responsive ?? {})) {
    responsive[key] = { ...responsive[key], ...patch };
  }
  if (appearance.logoSize) {
    responsive.logo = {
      ...responsive.logo,
      height: appearance.logoSize,
      maxHeight: appearance.logoSize,
      maxWidth: `calc(${appearance.logoSize} * ${LOGO_WIDTH_RATIO})`,
    };
  }

  const radii = STYLE_RADII[style];
  const parts = skin?.parts ??
    buildLayoutParts(solidSurface(colors, accentColor, radii), {
      headingColor,
      textColor,
      isMobile,
    });

  const selectionControl = appearance.selectionControl ?? DEFAULT_SELECTION_CONTROL;
  const drawsSwitches = layout.switchControl;
  const hostRadius = appearance.radius;
  const modalRadius = hostRadius || radii.modal;
  const buttonRadius = hostRadius || radii.button;

  // Glass draws its own dim over the variant styles; an admin overlay wins,
  // and a backdrop turned off leaves the page as it is (still blocking it).
  const overlayStyle: Style = !layout.dimBackdrop
    ? { ...styles.overlay, backgroundColor: "transparent" }
    : colors.overlay
      ? { ...styles.overlay, backgroundColor: colors.overlay }
      : styles.overlay;

  const position = modalPosition({
    layout,
    isMobile,
    radius: modalRadius,
    maxWidth: appearance.maxWidth,
  });
  const modalStyle: Style = {
    ...styles.modal,
    ...responsive.modal,
    borderRadius: modalRadius,
    ...(skin ? {} : { backgroundColor: colors.background || "#ffffff" }),
    ...position,
  };

  const button: Style = { borderRadius: buttonRadius };
  const acceptAll: Style = {
    ...button,
    backgroundColor:
      colors.accept_all_bg || brandAccent || content?.primary_color || DEFAULT_ACCENT_COLOR,
    color: colors.accept_all_text || "#ffffff",
  };
  // Accept Selected is outlined in the accent colour: still clearly an accept,
  // but subordinate to Accept All. Glass washes it in the accent instead: an
  // outline on a translucent sheet reads as a disabled button.
  const acceptSelected: Style = {
    ...button,
    backgroundColor: colors.accept_bg ?? (skin ? skin.palette.accentWash : "#ffffff"),
    color: colors.accept_text ?? accentColor,
    borderColor: skin ? "transparent" : colors.accept_text ?? accentColor,
  };
  const decline: Style = {
    ...button,
    backgroundColor: colors.decline_bg || (skin ? "transparent" : "#ffffff"),
    color: colors.decline_text || (skin ? textColor : "#000000"),
    borderColor:
      colors.decline_border ?? (colors.border || (skin ? skin.palette.outline : "#d0d5dd")),
  };

  const chip: Style = {
    borderRadius: buttonRadius,
    borderColor: colors.border || (skin ? skin.palette.hairline : "#d0d5dd"),
    backgroundColor: colors.language_bg || (skin ? skin.palette.control : "#ffffff"),
    color: colors.language_text || (skin ? headingColor : "#344054"),
  };
  // The menu and its rows take the chip's colours but never its radius, which
  // is sized for one short pill and bows a panel's border across the labels.
  const { borderRadius: _chipRadius, ...chipColors } = chip;
  const menu: Style = skin
    ? {
        ...(colors.language_bg && { backgroundColor: colors.language_bg }),
        color: chip.color,
      }
    : chipColors;
  const selected: Style = {
    ...menu,
    backgroundColor:
      colors.language_selected_bg || (skin ? skin.palette.accentWash : "#f3f4f6"),
    color: colors.language_selected_text || (skin ? headingColor : "#344054"),
    fontWeight: 600,
  };

  const rootAttributes: Record<string, string> = {
    [NOTICE_ROOT_ATTRIBUTE]: "",
    [NOTICE_ID_ATTRIBUTE]: noticeId,
    ...(skin ? { [APPEARANCE_ATTRIBUTE]: style } : {}),
    ...(drawsSwitches ? { [CONTROL_ATTRIBUTE]: SWITCH_CONTROL } : {}),
    ...(!layout.motion ? { [MOTION_ATTRIBUTE]: MOTION_OFF } : {}),
    ...buildTtsRootAttributes(colors),
  };

  return {
    appearance,
    layout,
    skin,
    styles,
    responsive: scaleFontSizes(responsive, textScale),
    parts: { ...parts, accordion: scaleFontSizes(parts.accordion, textScale) },
    sheetParts: skin?.parts ?? null,
    docked,
    showsLoaderPill: getAppearanceLayout(style).loaderPill,
    textScale,
    headingColor,
    textColor,
    accentColor,
    linkColor: colors.link || content?.secondary_color || DEFAULT_ACCENT_COLOR,
    radii,
    rootAttributes,
    cssVariables: {
      ...(skin?.cssVariables ?? {}),
      ...buildThemeCssVariables(colors),
    },
    overlayStyle,
    modalStyle,
    buttons: { base: button, acceptAll, acceptSelected, decline },
    language: { chip, menu, selected },
    controlSkin: skin?.controlSkin,
    confirmSkin: skin?.confirmSkin,
    selectionControl,
    drawsSwitches,
    roomyControls: drawsSwitches || selectionControl !== "checkbox",
  };
};

/** Attributes a docked notice root carries, for the dvh rule. */
export const dockAttributes = (look: ModalLook): Record<string, string> =>
  look.docked ? { [DOCK_ATTRIBUTE]: DOCKED } : {};
